import { Suspense, type FC } from 'react';
import { Route, useParams } from 'react-router-dom';
import { useMemoOne } from 'use-memo-one';
import {
  useOptionalCapacityManagementRoot,
  type CapacityManagementRootRequest,
  type OptionalCapacityManagementRootRequest,
} from 'src/apis/capacity-management/use-capacity-management-root';
import { useOptionalDirectFcfsCapacityPools } from 'src/apis/direct-fcfs/use-direct-fcfs-capacity-pools';
import type {
  NetworkPointParams,
  NetworkPointRequest,
} from 'src/apis/monolith/use-network-point';
import { useNetworkPoint } from 'src/apis/monolith/use-network-point';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { AddInternalBooking } from 'src/pages/transport/network-points/details/availability-and-price-data/internal-bookings/add';
import { EditInternalBooking } from 'src/pages/transport/network-points/details/availability-and-price-data/internal-bookings/edit';
import { AvailabilityAndPriceDataOverview } from 'src/pages/transport/network-points/details/availability-and-price-data/overview';
import { EditPriceSettings } from 'src/pages/transport/network-points/details/availability-and-price-data/price-settings/edit';
import { EditPriceSteps } from 'src/pages/transport/network-points/details/availability-and-price-data/price-steps/edit';
import { EditTechnicalCapacity } from 'src/pages/transport/network-points/details/availability-and-price-data/technical-capacity/edit';
import { CapacityOverview } from 'src/pages/transport/network-points/details/capacity-overview';
import { CapacityOverviewFcfs } from 'src/pages/transport/network-points/details/capacity-overview-fcfs';
import { MyPortfolioOverview } from 'src/pages/transport/network-points/details/my-portfolio-overview';
import { NetworkPointHeader } from 'src/pages/transport/network-points/details/network-point-header';
import { NetworkPointsDetails } from 'src/pages/transport/network-points/details/point-details';

export const NetworkPoint: FC = () => {
  const { oldNetworkPointId } = useParams<{ oldNetworkPointId: string }>();

  const networkPointParams = useMemoOne<NetworkPointParams>(
    () => ({ 'include-links': true }),
    []
  );
  const networkPoint = useNetworkPoint({
    oldNetworkPointId,
    params: networkPointParams,
  });

  const capacityManagementRoot = useOptionalCapacityManagementRoot();

  if (!networkPoint.response || !capacityManagementRoot.loaded)
    return <PageSpinner />;

  return (
    <RootRoutes>
      {isSuccessfulCapacityManagementRoot(capacityManagementRoot) && (
        <>
          <Route
            path="/transport/network-points/:oldNetworkPointId/availability-and-price-data/technical-capacity/edit"
            element={
              <EditTechnicalCapacity
                networkPoint={networkPoint}
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />

          <Route
            path="/transport/network-points/:oldNetworkPointId/availability-and-price-data/internal-bookings/add"
            element={
              <AddInternalBooking
                networkPoint={networkPoint}
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />

          <Route
            path="/transport/network-points/:oldNetworkPointId/availability-and-price-data/internal-bookings/:bookingId/edit"
            element={
              <EditInternalBooking
                networkPoint={networkPoint}
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />

          <Route
            path="/transport/network-points/:oldNetworkPointId/availability-and-price-data/price-settings/edit"
            element={
              <EditPriceSettings
                networkPoint={networkPoint}
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />

          <Route
            path="/transport/network-points/:oldNetworkPointId/availability-and-price-data/price-steps/edit"
            element={
              <EditPriceSteps
                networkPoint={networkPoint}
                capacityManagementRoot={capacityManagementRoot}
              />
            }
          />
        </>
      )}

      <Route
        path="*"
        element={
          <SiblingRoutes
            networkPoint={networkPoint}
            capacityManagementRoot={capacityManagementRoot}
          />
        }
      />
    </RootRoutes>
  );
};

const SiblingRoutes: FC<{
  networkPoint: Successful<NetworkPointRequest>;
  capacityManagementRoot: OptionalCapacityManagementRootRequest;
}> = ({ networkPoint, capacityManagementRoot }) => {
  const capacityPools = useOptionalDirectFcfsCapacityPools(
    networkPoint.response.data.networkPointUuid
  );

  if (!capacityPools.loaded) return <PageSpinner />;

  return (
    <div className="grid gap-md">
      <NetworkPointHeader
        networkPoint={networkPoint.response.data}
        capacityManagementRoot={capacityManagementRoot.response?.data}
        hasPublicCapacityInformation={Boolean(
          capacityPools.response?.data.length
        )}
      />

      <Suspense fallback={<PageSpinner />}>
        <RootRoutes>
          {isSuccessfulCapacityManagementRoot(capacityManagementRoot) && (
            <>
              <Route
                path="/transport/network-points/:oldNetworkPointId/availability-and-price-data"
                element={
                  <AvailabilityAndPriceDataOverview
                    networkPoint={networkPoint}
                    capacityManagementRoot={capacityManagementRoot}
                  />
                }
              />

              <Route
                path="/transport/network-points/:oldNetworkPointId/my-portfolio-overview"
                element={
                  <MyPortfolioOverview
                    networkPoint={networkPoint}
                    capacityManagementRoot={capacityManagementRoot}
                  />
                }
              />

              <Route
                path="/transport/network-points/:oldNetworkPointId/capacity-overview"
                element={
                  <CapacityOverview
                    networkPoint={networkPoint}
                    capacityManagementRoot={capacityManagementRoot}
                  />
                }
              />
            </>
          )}

          <Route
            path="/transport/network-points/:oldNetworkPointId/capacity-overview-fcfs"
            element={<CapacityOverviewFcfs networkPoint={networkPoint} />}
          />

          <Route
            path="/transport/network-points/:oldNetworkPointId"
            element={<NetworkPointsDetails networkPoint={networkPoint} />}
          />

          <Route path="*" element={<ThrowNotFound />} />
        </RootRoutes>
      </Suspense>
    </div>
  );
};

// small helper needed to stay compatible with the old code
function isSuccessfulCapacityManagementRoot(
  capacityManagementRoot: OptionalCapacityManagementRootRequest
): capacityManagementRoot is Successful<CapacityManagementRootRequest> {
  return capacityManagementRoot.response as any;
}
